import { Button, Checkbox, Divider, InputNumber, Select, Slider, Tabs } from 'antd';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { CaretLeftFilled } from '@ant-design/icons';

import AudioPlayButton from './audio-play-button';
import getLabelDisplayName from 'utils/label-display';
import AnnotationVoiceInput from 'components/voice-input/annotation-voice-input';

import { config_V2, getFilledFullText_V2, toDisplay_V2 } from '../phase2-annotation-conf';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { getRationalFps, frameToMs } from 'utils/media-time';
import { parseAndUpgradeAttribute, serializeAttribute } from 'utils/provenance-adapter';
import { Alert } from 'antd';

interface Props {
    croppedImages: any;
    audioBlobURL: string;
    states: any[];
    audio: React.MutableRefObject<HTMLAudioElement[]>;
    playAudio: (idx: number) => void;
    audiolisteners: any;
    audioPlayingIdx: number;
    frameSpeed: number;
    setAudioPlayingIdx: (idx: number) => void;
    setDoneButtonDisabled: (disabled: boolean) => void;
    onUpdateAnnotations: (states: any[]) => void;
    onUpdateJobAttributes(id: number, value: string): void;
    jobAttributeSpec: any[];
    jobAttributes: any;
    popupNumber: number;
    isRecurring: boolean;
    popupFrame: number;
    isLastRecurringPopup: boolean;
    jobInstance: any;
}

const DOESNOTAPPEAR_END = '.doesnotappear';





function TemporalErrorPanel({ errorDetails }: { errorDetails: string }) {
    return (
        <Alert
            message="Temporal Timing Integrity Warning"
            description={
                <div>
                    <p>
                        <strong>CRITICAL ERROR:</strong> A timing mismatch or data corruption has been detected in the recurring popup questionnaire responses.
                    </p>
                    <p>
                        To protect research integrity and database consistency, further submissions for this job are locked.
                    </p>
                    <p style={{ fontFamily: 'monospace', background: '#fff2f0', padding: '8px', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                        Details: {errorDetails}
                    </p>
                    <p>
                        Please take a screenshot of this error and contact the CVAT platform administrator immediately.
                    </p>
                </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
        />
    );
}

function Phase2bPopupQuestionnaire(props: Props) {
    const {
        states,
        audioBlobURL,
        audio,
        croppedImages,
        audiolisteners,
        audioPlayingIdx,
        frameSpeed,
        jobAttributeSpec,
        jobAttributes,
        playAudio,
        setAudioPlayingIdx,
        setDoneButtonDisabled,
        onUpdateAnnotations,
        onUpdateJobAttributes,
        popupNumber,
        isRecurring,
        popupFrame,
        isLastRecurringPopup,
        jobInstance,
    } = props;

    const { fpsNum, fpsDen, isStable } = getRationalFps(jobInstance?.dataFrameRate || 15);
    const [hasCorruptionError, setHasCorruptionError] = useState(false);
    const [corruptionMessage, setCorruptionMessage] = useState('');

    useEffect(() => {
        setDoneButtonDisabled(true);
    }, []);

    useEffect(() => {
        if (!hasCorruptionError) {
            setDoneButtonDisabled(false);
        }
        return () => setDoneButtonDisabled(true);
    }, [hasCorruptionError]);

    const RECURRING = 'recurring';

    const STARTS_WITH = 'phase2b.popup.' + (isRecurring ? RECURRING : popupNumber) + '.';
    const EXCLUDE_QUESTION = 'phase2b.popup.0.overallsummary'

    // Only V2 Exists for Phase2B.
    const config = config_V2;
    const getFilledFullText = getFilledFullText_V2;
    const toDisplay = toDisplay_V2;

    const [showDoesNotAppearError, setShowDoesNotAppearError] = useState<number[]>([]);
    const [frameToPopupNumber, setFrameToPopupNumber] = useState<{ [key: number]: number }>({});
    const doesNotAppearCheck = (states: any) => {
        const arr: number[] = []
        const anyTrue = states.some((state: any, index: number) => {
            let attributes = state.label.attributes.filter((attr: any) => attr.name.endsWith(DOESNOTAPPEAR_END));
            let values = attributes.map((attr: any) => state.attributes[attr.id]);
            let finalStateValue = values.every((val: any) => val === 'true');

            if (finalStateValue) {
                arr.push(index);
            }

            return finalStateValue; // .some will return true if any finalStateValue is true
        });
        setShowDoesNotAppearError(arr);
        if (anyTrue || hasCorruptionError) {
            setDoneButtonDisabled(true);
        }

    };

    useEffect(() => {
        doesNotAppearCheck(states);
    }, [states, hasCorruptionError])

    const getRecurringVal = (val: string, currentFrame: number) => {
        const payload = parseAndUpgradeAttribute(
            val,
            jobInstance?.startFrame || 0,
            fpsNum,
            fpsDen,
            isStable
        );
        if (payload.schema_version === -1) {
            if (!hasCorruptionError) {
                setHasCorruptionError(true);
                setCorruptionMessage('Malformed JSON payload or corrupted frame-keyed dictionary.');
                setDoneButtonDisabled(true);
            }
            return '';
        }
        // Match entry by target_ms (with 1000ms drift tolerance for VFR resilience)
        const localTargetMs = frameToMs(currentFrame, jobInstance?.startFrame || 0, fpsNum, fpsDen);
        const match = payload.entries.find(
            (entry) => Math.abs(entry.target_ms - localTargetMs) < 1000
        );
        return match ? match.value : '';
    };

    const getAttrVal = (attributes: any, attrId: number) =>
        isRecurring ? getRecurringVal(attributes[attrId], popupFrame) : attributes[attrId];

    const setAttrVal = (attributes: any, attrId: number, newVal: string) => {
        let val = newVal;
        if (isRecurring) {
            const payload = parseAndUpgradeAttribute(
                attributes[attrId],
                jobInstance?.startFrame || 0,
                fpsNum,
                fpsDen,
                isStable
            );
            if (payload.schema_version === -1) {
                setHasCorruptionError(true);
                setCorruptionMessage('Cannot save response: existing database payload is corrupted.');
                setDoneButtonDisabled(true);
                return attributes;
            }

            const localTargetMs = frameToMs(popupFrame, jobInstance?.startFrame || 0, fpsNum, fpsDen);
            const existingIndex = payload.entries.findIndex(
                (entry) => Math.abs(entry.target_ms - localTargetMs) < 1000
            );

            const updatedEntries = [...payload.entries];
            if (existingIndex !== -1) {
                updatedEntries[existingIndex] = {
                    ...updatedEntries[existingIndex],
                    value: newVal,
                };
            } else {
                updatedEntries.push({
                    id: `ms_${localTargetMs}`,
                    target_ms: localTargetMs,
                    initial_rendered_frame: popupFrame,
                    popup_type: 'phase2b.popup.recurring',
                    sequence_index: 0,
                    is_authoritative: isStable,
                    value: newVal,
                });
            }

            val = serializeAttribute(updatedEntries);
        }
        attributes = {
            ...attributes,
            [attrId]: val,
        };
        return attributes;
    };

    const [statesToShow, setStatesToShow] = useState([]);
    const checkDoesNotAppear = (states: any, currFrame: any) => {
        let Arr : any = [];

        states.forEach((state: any, index: number) => {

            if(!isLastRecurringPopup){
                let doesNotAppearVals = JSON.parse(state.doesNotAppearDefault || '{}');
                const matchingKey = Object.keys(doesNotAppearVals).find(
                    (key) => Math.abs(parseInt(key, 10) - currFrame) <= 1
                );
                let doesNotAppear = matchingKey ? doesNotAppearVals[matchingKey] : true;
                let attributes = state.label.attributes.filter((attr: any) => attr.name.includes(RECURRING+DOESNOTAPPEAR_END));
                let doesNotAppearAttr = attributes[0];
                if(doesNotAppear){
                    if (matchingKey) {
                        state.attributes = setAttrVal(state.attributes, doesNotAppearAttr.id, "true")
                    }
                }else{
                    Arr.push(state.serverID)
                    if (getAttrVal(state.attributes, doesNotAppearAttr.id) == '' && matchingKey) {
                        state.attributes = setAttrVal(state.attributes, doesNotAppearAttr.id, "false")
                    }
                }
            }
            else{
                // Only show people in the last group when appear in one of the popup.
                let attributes = state.label.attributes.filter((attr: any) => attr.name.includes(RECURRING+DOESNOTAPPEAR_END));
                let doesNotAppearAttr = attributes[0];
                const doesNotAppearData = state.attributes[doesNotAppearAttr.id] != '' ? JSON.parse(state.attributes[doesNotAppearAttr.id]) : {};

                if (
                    Object.keys(doesNotAppearData).length === 0 ||
                    !Object.values(doesNotAppearData).every((val) => val === 'true')
                ) {
                    Arr.push(state.serverID);
                }
            }
            onUpdateAnnotations([state]);
            setStatesToShow(Arr)
        });

    }

    const createFrameMap = (states: any) => {
        if (!states || !states.length || !states[0].doesNotAppearDefault) return;
        const frames = JSON.parse(states[0].doesNotAppearDefault);
        const sortedFrames = Object.keys(frames).sort((a, b) => parseInt(a) - parseInt(b));
        const newMap = sortedFrames.reduce((acc: { [key: number]: number }, key, index) => {
            acc[parseInt(key)] = index + 1;
            return acc;
        }, {});
        setFrameToPopupNumber(newMap);
    }

    const getPopupDisplayNumber = (frame: number) => {
        const matchingKey = Object.keys(frameToPopupNumber).find(
            (key) => Math.abs(parseInt(key, 10) - frame) <= 1
        );
        return matchingKey ? frameToPopupNumber[parseInt(matchingKey, 10)] : '?';
    };

    useEffect(() => {
        createFrameMap(states);
        checkDoesNotAppear(states, popupFrame);
    }, [popupFrame]);

    return (
        <>{hasCorruptionError && (
            <TemporalErrorPanel errorDetails={corruptionMessage} />
        )}
        <Title level={3}>Popup #{getPopupDisplayNumber(popupFrame)} (Frame: {popupFrame})</Title>
        <Tabs
            type='card'
            tabBarStyle={{ marginBottom: '0px' }}
            onChange={() => {
                audio.current.forEach((rf) => rf.pause());
                setAudioPlayingIdx(-1);
            }}
        >
            {states.map((state, idx) => (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>
                                {getLabelDisplayName(state.label.name)}{' '}
                                {
                                    state.attributes[
                                        state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                    ]
                                }
                            </Text>
                            {
                            // doesnotappear is unchecked, if it exists
                                ((
                                    isLastRecurringPopup ?
                                    // If final popup, see if appear in any frame
                                    statesToShow.includes(state.serverID) :
                                    // If not final popup, see if appear in this frame
                                    (state.label.attributes.every(
                                        (attr: any) =>
                                            attr.name === STARTS_WITH + 'doesnotappear' ?
                                        ['false', ''].includes(getAttrVal(state.attributes, attr.id)) : true,
                                    ))
                                ) &&
                                    // non-nullable attributes are unfilled
                                    state.label.attributes
                                        .filter((attr: any) => attr.name.startsWith(STARTS_WITH))
                                        .some((attr: any) => getAttrVal(state.attributes, attr.id) === '') ||
                                    (showDoesNotAppearError.includes(idx)))
                                && (
                                    <CaretLeftFilled style={{ color: 'red' }} />
                                )}
                        </span>
                    }
                    key={state.clientID}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                        {
                            !(statesToShow.includes(state.serverID)) && (
                                <div>
                                    This person either does not appear in the previous section or does not contribute meaningfully to this interaction.
                                    If you think this is a mistake, please report this job.
                                </div>
                            )
                        }
                        {
                            statesToShow.includes(state.serverID)  &&

                            <>
                            {state.label.attributes
                                .filter((attr: any) => attr.name.startsWith(STARTS_WITH))
                                .sort((a:any, b:any)=>{
                                    const orderA = Object.keys(config).indexOf(a.name);
                                    const orderB = Object.keys(config).indexOf(b.name);
                                    return orderA - orderB;
                                })
                                .map((attr: any) => (
                                    (// doesnotappear is unchecked, if it exists
                                    state.label.attributes.every(
                                        (attr: any) =>
                                            attr.name === STARTS_WITH + 'doesnotappear' ?
                                        ['false', ''].includes(getAttrVal(state.attributes, attr.id)) : true,
                                    ) ||
                                    // or this is the doesnotappear checkbox attribute (always show)
                                    attr.name.endsWith(DOESNOTAPPEAR_END)
                                    )
                                    &&
                                    <div>
                                        {showDoesNotAppearError.includes(idx) && (
                                            <div>
                                                <Text type="danger">The "does not appear" checkbox has been checked for all popups of {getLabelDisplayName(state.label.name)} {state.attributes[
                                                    state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                                ]}.
                                                    This is not allowed. Please uncheck the "does not appear" checkbox for at least one popup of {getLabelDisplayName(state.label.name)} {state.attributes[
                                                        state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                                    ]}.
                                                </Text>
                                            </div>
                                        )}
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{getFilledFullText(attr.name, state.label.name)}</Text>{' '}
                                            {

                                            // non-nullable attr has no value
                                            getAttrVal(state.attributes, attr.id) === '' && !attr.nullable && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>

                                        <div>
                                            {attr.inputType === 'checkbox' && (
                                                <Checkbox
                                                    onChange={(event: CheckboxChangeEvent): void => {
                                                        state.attributes = setAttrVal(
                                                            state.attributes,
                                                            attr.id,
                                                            event.target.checked ? 'true' : 'false'
                                                        );
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    checked={getAttrVal(state.attributes, attr.id) === 'true'}
                                                />
                                            )}
                                            {attr.inputType === 'number' && (
                                                <Row gutter={[16, 32]}>
                                                    <Col sm={4}>
                                                    </Col>
                                                    <Col sm={12}>
                                                        <Slider
                                                            included={false}
                                                            marks={{
                                                                ...Array((parseFloat(attr.values[1]) || 5) - (parseFloat(attr.values[0]) || 1) + 1).fill(0).reduce((prev, item, idx) => {
                                                                    prev[idx + (parseFloat(attr.values[0]) || 1)] = '';
                                                                    return prev
                                                                }, {}),

                                                                [parseFloat(attr.values[0]) || 1]: config[attr.name]?.low || '',
                                                                [((parseFloat(attr.values[0]) || 1) + (parseFloat(attr.values[1]) || 5))/2]: 'Neutral',
                                                                [parseFloat(attr.values[1]) || 5]: config[attr.name]?.high || '',
                                                            }}
                                                            min={parseFloat(attr.values[0]) || 1}
                                                            max={parseFloat(attr.values[1]) || 5}
                                                            step={parseFloat(attr.values[2]) || 1}
                                                            onChange={(val) => {
                                                                state.attributes = setAttrVal(
                                                                    state.attributes,
                                                                    attr.id,
                                                                    ''+val
                                                                );
                                                                onUpdateAnnotations([states[idx]]);
                                                            }}
                                                            value={
                                                                getAttrVal(state.attributes, attr.id) != ''
                                                                    ? parseInt(getAttrVal(state.attributes, attr.id))
                                                                    : 3
                                                            }
                                                            style={{ width: '100%' }}
                                                        />
                                                </Col>
                                                <Col offset={2} span={4}>
                                                    <InputNumber
                                                    min={parseFloat(attr.values[0]) || 1}
                                                    max={parseFloat(attr.values[1]) || 5}
                                                    step={parseFloat(attr.values[2]) || 1}
                                                    style={{ margin: '0 16px' }}
                                                    value={getAttrVal(state.attributes, attr.id)
                                                        ? parseInt(getAttrVal(state.attributes, attr.id))
                                                        : 3}
                                                    disabled = {true}
                                                    onChange={(val) => {
                                                        state.attributes = setAttrVal(
                                                            state.attributes,
                                                            attr.id,
                                                            ''+val
                                                        );
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    />
                                                </Col>
                                                </Row>
                                            )}
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        state.attributes = setAttrVal(
                                                            state.attributes,
                                                            attr.id,
                                                            ''+val
                                                        );
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={getAttrVal(state.attributes, attr.id)}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        state.attributes = setAttrVal(
                                                            state.attributes,
                                                            attr.id,
                                                            val
                                                            .map((valitem: string) => valitem.trim())
                                                            .join(),
                                                        );
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={
                                                        getAttrVal(state.attributes, attr.id)
                                                            ? getAttrVal(state.attributes, attr.id)
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'text' && (
                                                <AnnotationVoiceInput
                                                    value={getAttrVal(state.attributes, attr.id)}
                                                    onChange={(nextValue) => {
                                                        state.attributes = setAttrVal(
                                                            state.attributes,
                                                            attr.id,
                                                            `${nextValue}`,
                                                        );
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    context={{
                                                        jobId: jobInstance.id,
                                                        phase: jobInstance.phase,
                                                        attributeSpecId: attr.id,
                                                        attributeName: attr.name,
                                                        targetScope: 'object',
                                                        targetServerId: state.serverID,
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <Divider style={{marginTop: '36px'}}></Divider>
                                    </div>
                                ))}
                            </>
                        }
                                <Row style={{height: '32px'}}></Row>
                        </Col>
                        <Col span={6}>
                            {states[idx].objectType === 'audioselection' && audioBlobURL && (
                                <>
                                    <AudioPlayButton
                                        audioBlobURL={audioBlobURL}
                                        audio={audio}
                                        audioPlayingIdx={audioPlayingIdx}
                                        frameSpeed={frameSpeed}
                                        audiolisteners={audiolisteners}
                                        idx={idx}
                                        playAudio={playAudio}
                                        setAudioPlayingIdx={setAudioPlayingIdx}
                                        state={states[idx]}
                                    />
                                </>
                            )}
                            {states[idx].objectType === 'shape' && croppedImages[idx] && (
                                <img style={{ maxWidth: '100%', height: '150px' }} src={croppedImages[idx]}></img>
                            )}
                        </Col>
                    </Row>
                </Tabs.TabPane>
            ))}

            {jobAttributeSpec.filter((spec) => spec.name.startsWith(STARTS_WITH)).length && (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>Situation Labels</Text>
                            {jobAttributeSpec
                                .filter((spec) => spec.name.startsWith(STARTS_WITH) && !spec.name.includes(EXCLUDE_QUESTION))
                                .some((attr: any) => getAttrVal(jobAttributes, attr.id) === '') && (
                                <CaretLeftFilled style={{ color: 'red' }} />
                            )}
                        </span>
                    }
                    key={9999999999}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            {jobAttributeSpec
                                .filter((attr: any) => attr.name.startsWith(STARTS_WITH) && !attr.name.includes(EXCLUDE_QUESTION))
                                .toSorted((a: any, b: any) => a.id - b.id)
                                .map((attr: any) => (
                                    <div>
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{config[attr.name]?.fullText}</Text>{' '}
                                            {getAttrVal(jobAttributes, attr.id) === '' && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>
                                        <div>
                                            {attr.inputType === 'number' && (
                                                <Row gutter={[16, 32]}>
                                                <Col sm={4}>
                                                </Col>
                                                <Col sm={12}>
                                                    <Slider
                                                        included={false}
                                                        marks={{
                                                            [parseFloat(attr.values[0]) || 1]: config[attr.name]?.low || '',
                                                            [((parseFloat(attr.values[0]) || 1) + (parseFloat(attr.values[1]) || 5))/2]: 'Neutral',
                                                            [parseFloat(attr.values[1]) || 5]: config[attr.name]?.high || '',
                                                        }}
                                                        min={parseFloat(attr.values[0]) || 1}
                                                        max={parseFloat(attr.values[1]) || 5}
                                                        step={parseFloat(attr.values[2]) || 1}
                                                        onChange={(val) => {
                                                            onUpdateJobAttributes(attr.id, '' + val);
                                                        }}
                                                        value={getAttrVal(jobAttributes, attr.id)}
                                                        style={{ width: '100%' }}
                                                    />
                                            </Col>
                                            <Col offset={2} span={4}>
                                                <InputNumber
                                                min={parseFloat(attr.values[0]) || 1}
                                                max={parseFloat(attr.values[1]) || 5}
                                                step={parseFloat(attr.values[2]) || 1}
                                                style={{ margin: '0 16px' }}
                                                onChange={(val) => {
                                                    onUpdateJobAttributes(attr.id, '' + val);
                                                }}
                                                value={getAttrVal(jobAttributes, attr.id)}
                                                disabled = {true}
                                                />
                                            </Col>
                                            </Row>
                                            )}
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        onUpdateJobAttributes(attr.id, val);
                                                    }}
                                                    value={getAttrVal(jobAttributes, attr.id)}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        onUpdateJobAttributes(
                                                            attr.id,
                                                            val.map((valitem: string) => valitem.trim()).join(),
                                                        );
                                                    }}
                                                    value={
                                                        getAttrVal(jobAttributes, attr.id)
                                                            ? getAttrVal(jobAttributes, attr.id)
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'text' && (
                                                <AnnotationVoiceInput
                                                    value={getAttrVal(jobAttributes, attr.id)}
                                                    onChange={(nextValue) => {
                                                        onUpdateJobAttributes(attr.id, nextValue);
                                                    }}
                                                    context={{
                                                        jobId: jobInstance.id,
                                                        phase: jobInstance.phase,
                                                        attributeSpecId: attr.id,
                                                        attributeName: attr.name,
                                                        targetScope: 'job',
                                                        targetServerId: null,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </Col>
                    </Row>
                    <Row style={{height: '32px'}}></Row>
                </Tabs.TabPane>
            )}
        </Tabs></>
    );
}

export default Phase2bPopupQuestionnaire;
